import {
  MetafieldFilter,
  ProductCollectionSortKeys,
  VariantOptionFilter,
  ProductFilter,
  Filter,
  FilterValue,
} from '@shopify/hydrogen-react/storefront-api-types';

export interface CollectionSortParams {
  type: ProductCollectionSortKeys;
  reverse?: boolean;
}

export enum SortOptions {
  BEST_SELLING = 'BEST_SELLING',
  COLLECTION_DEFAULT = 'COLLECTION_DEFAULT',
  PRICE = 'PRICE',
  RELEVANCE = 'RELEVANCE',
  TITLE = 'TITLE',
}
export enum SortOptionsLabels {
  BEST_SELLING = 'Best Selling',
  COLLECTION_DEFAULT = 'Collection',
  PRICE = 'Price: Low-High',
  PRICE_REVERSE = 'Price: High-Low',
  RELEVANCE = 'Relevance',
  TITLE = 'Title: A-Z',
  TITLE_REVERSE = 'Title: Z-A',
}

export enum FiltersKeys {
  CATEGORY = 'filter.p.product_type',
  BRAND = 'filter.p.vendor',
  COLOR = 'filter.v.option.color',
  SIZE = 'filter.v.option.size',
  LOGO = 'filter.v.option.logo',
}

export const formatUrlQuery = (data: ProductFilter[], sort?: SortOptionsLabels | undefined) => {
  const queryParams = new URLSearchParams();

  data.forEach(item => {
    for (const key in item) {
      // eslint-disable-next-line no-prototype-builtins
      if (item.hasOwnProperty(key)) {
        const value = item[key];
        if (typeof value === 'object') {
          // Handle object values (like variantOption and productMetafield)
          if (key === 'variantOption') {
            const subKey = `${key}.${value.name}`;
            queryParams.append(subKey, value.value);
          } else if (key === 'productMetafield') {
            const subKey = `${key}.${value.namespace}.${value.key}`;
            queryParams.append(subKey, value.value);
          }
        } else {
          // Handle string values
          queryParams.append(key, value);
        }
      }
    }
  });

  if (sort) {
    queryParams.append('sort', sort);
  }

  return queryParams.toString();
};

export function getSortFromURL(queryString: string): CollectionSortParams | undefined {
  const sortParam = new URLSearchParams(queryString).get('sort') as SortOptionsLabels | null;
  if (!sortParam || !Object.values(SortOptionsLabels).includes(sortParam as SortOptionsLabels)) {
    return undefined;
  }

  if (sortParam === SortOptionsLabels.PRICE_REVERSE) {
    return { type: SortOptions.PRICE as ProductCollectionSortKeys, reverse: true };
  }

  if (sortParam === SortOptionsLabels.TITLE_REVERSE) {
    return { type: SortOptions.TITLE as ProductCollectionSortKeys, reverse: true };
  }

  return {
    type: sortParam as unknown as ProductCollectionSortKeys,
    reverse: false,
  };
}

export const getFilterFromURL = (queryString: string) => {
  let params = new URLSearchParams(queryString);
  let result: any[] = [];

  params.forEach((value, key) => {
    if (key.startsWith('variantOption')) {
      const variantName = key.split('.')[1];

      let obj = {
        variantOption: {
          name: variantName,
          value: value,
        },
      };
      result.push(obj);
    }
    if (key === 'productVendor' || key === 'productType' || key === 'tag') {
      let obj = {};
      obj[key] = value;
      result.push(obj);
    }
    if (key.startsWith('productMetafield')) {
      const parts = key.split('.');
      if (parts.length === 3) {
        const namespace = parts[1];
        const fieldKey = parts[2];
        result.push({
          productMetafield: {
            namespace: namespace,
            key: fieldKey,
            value: value,
          },
        });
      }
    }
  });

  return result;
};

export function getOrderedFilters(filters: Filter[], filtersOrder: FiltersKeys[]): Filter[] {
  const filtered = filters.filter(filter => {
    return (
      !filter.id.includes('filter.p.m.custom') &&
      filter.values.length > 0 &&
      filter.id !== 'filter.p.tag' &&
      filter.type !== 'BOOLEAN'
    );
  });

  return filtered.sort((a, b) => {
    const indexA = filtersOrder.indexOf(a.id as FiltersKeys);
    const indexB = filtersOrder.indexOf(b.id as FiltersKeys);

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    return 0;
  });
}

export function getAvailableFilters(filters: Filter[]): Filter[] {
  return filters.map(filter => {
    filter.values = getAvailableFilterValues(filter.values);
    return filter;
  });
}

export function getAvailableFilterValues(filterValues: FilterValue[]): FilterValue[] {
  return filterValues.filter(value => {
    return value.count > 0;
  });
}

export const handleFilter = (filterItem: ProductFilter, filter: ProductFilter[]) => {
  const list = [...filter];
  const itemKey: string = Object.keys(filterItem)[0];
  const itemValue = Object.values(filterItem)[0] as VariantOptionFilter | string | MetafieldFilter;

  let existingItemIndex: number = -1;

  if (typeof itemValue === 'string') {
    existingItemIndex = list.findIndex(item => item[itemKey] === itemValue);
  }

  if (typeof itemValue === 'object') {
    existingItemIndex = list.findIndex(item => {
      if (!item[itemKey]) {
        return false;
      }
      if ('key' in item[itemKey] && item[itemKey].key === 'exclusive') {
        return true;
      }
      if (item[itemKey]) {
        const object = item[itemKey] as VariantOptionFilter;
        return object.value === itemValue.value && object.name === itemValue['name'];
      }
    });
  }

  if (existingItemIndex !== -1) {
    list.splice(existingItemIndex, 1);
    return list;
  }

  list.push(filterItem);

  return list;
};
